import React from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { SheetTrigger } from '@/components/shadcn/sheet'
import { cn } from '@/lib/utils'

interface TooltipWrapperProps {
  /**
   * The element to wrap with a tooltip
   */
  children: React.ReactNode

  /**
   * The tooltip text to display
   */
  tooltip: string

  /**
   * Optional className for the tooltip content
   */
  contentClassName?: string

  /**
   * If true, the tooltip will be applied to a component that uses a trigger prop
   * (like FormSheet, Dialog, etc.)
   */
  wrapTrigger?: boolean

  /**
   * If wrapTrigger is true, this is the component that has a trigger prop
   */
  triggerComponent?: React.ReactElement
}

/**
 * TooltipWrapper - A flexible component for adding tooltips to any element
 *
 * Can be used in two ways:
 * 1. Simple wrapping: <TooltipWrapper tooltip="Help text">Button</TooltipWrapper>
 * 2. With trigger components:
 *    <TooltipWrapper
 *      tooltip="Edit item"
 *      wrapTrigger
 *      triggerComponent={<FormSheet trigger={undefined}>Content</FormSheet>}
 *    >
 *      <Button>Edit</Button>
 *    </TooltipWrapper>
 */
export function TooltipWrapper({
  children,
  tooltip,
  contentClassName = 'bg-foreground text-xs text-offwhite',
  wrapTrigger = false,
  triggerComponent,
}: TooltipWrapperProps) {
  // Simple tooltip wrapping
  if (!wrapTrigger) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className={cn(contentClassName)}>{tooltip}</TooltipContent>
      </Tooltip>
    )
  }

  // For components that use a trigger prop (like FormSheet)
  if (wrapTrigger && triggerComponent) {
    return (
      <Tooltip>
        {React.cloneElement(triggerComponent, {
          trigger: (
            <TooltipTrigger asChild>
              <SheetTrigger asChild>{children}</SheetTrigger>
            </TooltipTrigger>
          ),
          triggerIncludesSheetTrigger: true,
        })}
        <TooltipContent className={cn(contentClassName)}>{tooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return null
}
